// VideoFirebaseService.js - Version corrigée avec découverte indépendante du thème

import { collection, doc, addDoc, getDocs, getDoc, setDoc, deleteDoc, updateDoc, query, where, orderBy, limit, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import sourceService from '../CP-Sourcing/SourceService';
import { askGPT } from '../components/OpenAIService';

class VideoFirebaseService {
  constructor() {
    // Cache simple et léger
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes seulement

    // Configuration thématique simplifiée
    this.themes = {
      'entrepreneurship': {
        keywords: ['startup', 'entrepreneur', 'business', 'innovation'],
        searchTerms: 'startup entrepreneur business innovation formation',
        categories: ['business', 'education']
      },
      'technology': {
        keywords: ['programmation', 'développement', 'IA', 'tech'],
        searchTerms: 'programmation développement tech formation',
        categories: ['technology', 'education']
      },
      'finance': {
        keywords: ['finance', 'investissement', 'trading', 'crypto'],
        searchTerms: 'finance investissement formation',
        categories: ['finance', 'business']
      },
      'leadership': {
        keywords: ['leadership', 'management', 'équipe', 'coaching'],
        searchTerms: 'leadership management formation',
        categories: ['business', 'management']
      },
      'health': {
        keywords: ['santé', 'bien-être', 'nutrition', 'fitness'],
        searchTerms: 'santé bien-être nutrition fitness',
        categories: ['health', 'wellness']
      },
      'creativity': {
        keywords: ['créativité', 'design', 'art', 'photographie'],
        searchTerms: 'créativité design art formation',
        categories: ['arts', 'design']
      }
    };
  }

  // ================================
  // RÉCUPÉRATION DES VIDÉOS
  // ================================

  async getVideosByTheme(theme, maxVideos = 50, userId = null) {
    try {
      const cacheKey = `theme_${theme}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached.map(this.enhanceVideoData);

      console.log(`🎨 Chargement vidéos thème: ${theme}`);

      const q = query(
        collection(db, 'videos'),
        where('theme', '==', theme),
        where('status', '==', 'active'),
        orderBy('addedAt', 'desc'),
        limit(maxVideos)
      );

      const snapshot = await getDocs(q);
      const videos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      this.setCache(cacheKey, videos);
      return videos.map(this.enhanceVideoData);

    } catch (error) {
      console.error(`❌ Erreur thème ${theme}:`, error);
      return [];
    }
  }

  async getAllVideos(maxVideos = 200) {
    try {
      const cacheKey = 'all_videos';
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;

      console.log('📚 Chargement toutes vidéos');

      const q = query(
        collection(db, 'videos'),
        where('status', '==', 'active'),
        orderBy('addedAt', 'desc'),
        limit(maxVideos)
      );

      const snapshot = await getDocs(q);
      const videos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      this.setCache(cacheKey, videos);
      return videos.map(this.enhanceVideoData);

    } catch (error) {
      console.error('❌ Erreur getAllVideos:', error);
      return [];
    }
  }

  // ================================
  // RECHERCHE SIMPLIFIÉE
  // ================================

  async searchVideosByQuery(query, userId, options = {}) {
    const { maxResults = 30, useSemanticAnalysis = false } = options;
    
    try {
      console.log(`🔍 Recherche: "${query}"`);
      
      // Recherche textuelle simple
      const results = await this.searchByKeywords(query, { limit: maxResults });
      
      return {
        results: results,
        type: useSemanticAnalysis ? 'semantic' : 'textual',
        hasSemanticOption: true
      };
      
    } catch (error) {
      console.error('❌ Erreur recherche:', error);
      return { results: [], type: 'error', error: error.message };
    }
  }

  async searchByKeywords(query, options = {}) {
    const { limit = 30 } = options;
    
    try {
      const keywords = query.toLowerCase().split(' ').filter(w => w.length > 2);
      const allVideos = await this.getAllVideos(500);
      
      const filteredVideos = allVideos.filter(video => {
        const content = `${video.title} ${video.description || ''}`.toLowerCase();
        return keywords.some(keyword => content.includes(keyword));
      });
      
      // Score simple de pertinence
      const scoredVideos = filteredVideos.map(video => {
        const titleContent = video.title.toLowerCase();
        const fullContent = `${video.title} ${video.description || ''}`.toLowerCase();
        let score = 0;
        
        keywords.forEach(keyword => {
          if (titleContent.includes(keyword)) score += 3;
          else if (fullContent.includes(keyword)) score += 1;
        });
        
        return { ...video, textualScore: score };
      });
      
      return scoredVideos
        .sort((a, b) => b.textualScore - a.textualScore)
        .slice(0, limit);
      
    } catch (error) {
      console.error('❌ Erreur recherche textuelle:', error);
      return [];
    }
  }

  // ================================
  // DÉCOUVERTE DE VIDÉOS INDÉPENDANTE DU THÈME
  // ================================

  async discoverVideosByTheme(theme, userId, options = {}) {
    const { maxVideos = 20, searchQuery = null } = options;
    
    try {
      console.log(`🔍 Découverte thématique: ${theme}`);
      
      const themeConfig = this.themes[theme];
      if (!themeConfig) {
        throw new Error(`Thème ${theme} non configuré`);
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Utilisateur non authentifié');
      
      const token = await user.getIdToken(true);

      // Construire les termes de recherche
      let searchTerms = searchQuery || themeConfig.searchTerms;
      if (searchQuery) {
        searchTerms = `${searchQuery} ${themeConfig.keywords.slice(0, 2).join(' ')}`;
      }

      // Appel au service de sourcing avec récupération des scores et images
      const searchResult = await sourceService.getRecommendedSources({
        userId: userId,
        userObjective: `Vidéos ${theme}: ${searchQuery || themeConfig.searchTerms}`,
        query: searchTerms,
        languages: ['fr'],
        country: 'FR',
        maxSources: maxVideos,
        selectedProviders: ['YouTube', 'Google'],
        videoSearch: true,
        categories: themeConfig.categories,
        qualityFilter: 'medium',
        // 🆕 Paramètres pour récupérer scores et métadonnées
        includeScoring: true,
        includeMetadata: true,
        verifyAccessibility: true
      }, token);

      if (!searchResult?.sources?.length) {
        return { discovered: 0, added: 0, videos: [] };
      }

      // Transformation et ajout des vidéos AVEC les scores et images originales
      const discoveredVideos = this.transformSourcesToVideos(searchResult.sources, theme);
      const addedVideos = [];
      
      for (const video of discoveredVideos.slice(0, maxVideos)) {
        try {
          const exists = await this.checkVideoExists(video.url);
          if (!exists) {
            const videoId = await this.addVideoToDatabase(video, userId);
            addedVideos.push({ ...video, id: videoId });
          }
        } catch (error) {
          console.warn('⚠️ Erreur ajout vidéo:', error);
        }
      }

      // Nettoyer le cache pour ce thème
      this.clearCache(`theme_${theme}`);
      
      return {
        discovered: discoveredVideos.length,
        added: addedVideos.length,
        videos: addedVideos
      };

    } catch (error) {
      console.error(`❌ Erreur découverte ${theme}:`, error);
      return { discovered: 0, added: 0, videos: [], error: error.message };
    }
  }

  // ================================
  // GESTION OBJECTIFS - RECHERCHE ET DECOUVERTE
  // ================================

  async getVideosByObjective(objectiveId, userId, options = {}) {
    try {
      console.log(`🎯 Recherche vidéos pour objectif: ${objectiveId}`);
      console.log(`👤 UserId: ${userId}`);
      
      // Requête SANS orderBy pour éviter le problème d'index
      const q = query(
        collection(db, 'videos_objective'),
        where('userId', '==', userId),
        where('objectiveId', '==', objectiveId),
        where('status', '==', 'active'),
        limit(options.limit || 50)
      );

      console.log('🔍 Exécution requête sans orderBy...');
      const snapshot = await getDocs(q);
      console.log(`📊 ${snapshot.size} document(s) trouvé(s)`);
      
      if (!snapshot.empty) {
        const objectiveVideos = [];
        const docs = snapshot.docs;
        
        // Tri manuel par semanticScore (remplace orderBy)
        docs.sort((a, b) => (b.data().semanticScore || 0) - (a.data().semanticScore || 0));
        
        for (const docSnap of docs) {
          const data = docSnap.data();
          console.log('📄 Document trouvé:', {
            id: docSnap.id,
            videoId: data.videoId,
            semanticScore: data.semanticScore
          });
          
          const videoDoc = await this.getVideoById(data.videoId);
          if (videoDoc) {
            objectiveVideos.push({
              ...this.enhanceVideoData(videoDoc),
              semanticScore: data.semanticScore,
              semanticJustification: data.justification,
              analyzedAt: data.analyzedAt
            });
          } else {
            console.warn(`⚠️ Vidéo ${data.videoId} introuvable`);
          }
        }
        
        console.log(`✅ ${objectiveVideos.length} vidéos pré-analysées trouvées`);
        return objectiveVideos;
      } else {
        console.log('📭 Aucun document trouvé pour cet objectif');
        // NOUVEAU: Retourner un indicateur spécial au lieu du fallback
        return { 
          isEmpty: true, 
          needsDiscovery: true,
          objectiveId: objectiveId 
        };
      }
      
    } catch (error) {
      console.error('❌ Erreur getVideosByObjective:', error);
      console.error('Code erreur:', error.code);
      console.error('Message:', error.message);
      
      // NOUVEAU: Retourner un indicateur d'erreur au lieu du fallback
      return { 
        isEmpty: true, 
        needsDiscovery: true,
        objectiveId: objectiveId,
        error: error.message
      };
    }
  }


  // 🆕 DÉCOUVERTE POUR OBJECTIF 
  async discoverAndAnalyzeForObjective(objective, userId, options = {}) {
    const { maxVideos = 20 } = options;
    
    try {
      console.log(`🎯 Découverte DIRECTE pour objectif: "${objective.title}"`);
      
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Utilisateur non authentifié');
      
      const token = await user.getIdToken(true);

      // 🆕 RECHERCHE DIRECTE basée sur l'objectif, SANS mapping thématique
      const searchResult = await sourceService.getRecommendedSources({
        userId: userId,
        userObjective: `${objective.title}`,
        query: `${objective.title}`,
        languages: ['fr', 'en'],
        country: 'FR',
        maxSources: maxVideos,
        selectedProviders: ['YouTube', 'Google'],
        videoSearch: true,
        contentType: 'video',
        searchType: 'video', // Forcer la recherche vidéo
        fileType: 'video',
        qualityFilter: 'high',
        timeRange: 'recent',
        // 🆕 Paramètres pour récupérer scores et métadonnées
        includeScoring: true,
        includeMetadata: true,
        verifyAccessibility: true,
        sortBy: 'relevance'
      }, token);

      if (!searchResult?.sources?.length) {
        return {
          success: false,
          discovered: 0,
          added: 0,
          analyzed: 0
        };
      }

      // Transformer les sources avec les scores originaux
      const discoveredVideos = this.transformSourcesToVideosForObjective(
        searchResult.sources, 
        objective
      );
      
      const addedVideos = [];
      let linkedCount = 0;
      
      for (const video of discoveredVideos.slice(0, maxVideos)) {
        try {
          const exists = await this.checkVideoExists(video.url);
          if (!exists) {
            // Passer le contexte de l'objectif lors de l'ajout
            const videoId = await this.addVideoToDatabase(video, userId, null, objective);
            addedVideos.push({ ...video, id: videoId });
            linkedCount++;
          } else {
            // Si la vidéo existe déjà, créer la liaison avec l'objectif
            const existingVideo = await this.getVideoByUrl(video.url);
            if (existingVideo) {
              const linked = await this.linkVideoToObjective(existingVideo.id, objective, userId, video.relevanceScore);
              if (linked) linkedCount++;
            }
          }
        } catch (error) {
          console.warn('⚠️ Erreur ajout vidéo:', error);
        }
      }
      
      return {
        success: addedVideos.length > 0 || linkedCount > 0,
        discovered: discoveredVideos.length,
        added: addedVideos.length,
        analyzed: linkedCount
      };
      
    } catch (error) {
      console.error('❌ Erreur découverte objectif:', error);
      return { success: false, error: error.message };
    }
  }

  // 🆕 Nouvelle fonction pour créer la liaison vidéo-objectif
  async linkVideoToObjective(videoId, objective, userId, semanticScore = null) {
    try {
      // Vérifier si la liaison existe déjà
      const existingLinkQuery = query(
        collection(db, 'videos_objective'),
        where('userId', '==', userId),
        where('objectiveId', '==', objective.id),
        where('videoId', '==', videoId),
        limit(1)
      );
      
      const existingSnapshot = await getDocs(existingLinkQuery);
      
      if (!existingSnapshot.empty) {
        console.log(`🔗 Liaison déjà existante pour vidéo ${videoId} et objectif ${objective.id}`);
        return false;
      }
      
      // Créer la nouvelle liaison
      await addDoc(collection(db, 'videos_objective'), {
        userId: userId,
        objectiveId: objective.id,
        videoId: videoId,
        semanticScore: semanticScore || 7,
        justification: `Vidéo découverte automatiquement pour l'objectif "${objective.title}"`,
        analyzedAt: new Date().toISOString(),
        status: 'active',
        discoveryMethod: 'automatic'
      });
      
      console.log(`✅ Liaison créée: vidéo ${videoId} → objectif ${objective.id}`);
      return true;
      
    } catch (error) {
      console.error('❌ Erreur création liaison vidéo-objectif:', error);
      return false;
    }
  }

  // 🆕 Fonction utilitaire pour récupérer une vidéo par URL
  async getVideoByUrl(url) {
    try {
      const q = query(
        collection(db, 'videos'),
        where('url', '==', url),
        limit(1)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.warn('⚠️ Erreur récupération vidéo par URL:', error);
      return null;
    }
  }

  // ================================
  // GESTION CRUD AMÉLIORÉE
  // ================================

  async getVideoById(videoId) {
    try {
      const q = query(
        collection(db, 'videos'),
        where('__name__', '==', videoId),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.warn('⚠️ Erreur getVideoById:', videoId, error);
      return null;
    }
  }

  // 🆕 VERSION AMÉLIORÉE avec gestion scores et liaison objectif
  async addVideoToDatabase(videoData, userId, theme = null, objectiveContext = null) {
    try {
      const videoToAdd = {
        ...videoData,
        addedBy: userId,
        addedAt: new Date().toISOString(),
        status: 'active',
        totalViews: 0,
        discoveryMethod: 'sourceservice',
        lastUpdated: new Date().toISOString(),
        // 🆕 Utiliser le thème détecté ou celui passé en paramètre
        theme: videoData.theme || theme || 'general'
      };

      const docRef = await addDoc(collection(db, 'videos'), videoToAdd);
      const videoId = docRef.id;
      
      console.log(`✅ Vidéo ajoutée: ${videoId}`);
      
      // 🆕 Si un contexte d'objectif est fourni, créer la liaison
      if (objectiveContext && objectiveContext.id) {
        await this.linkVideoToObjective(videoId, objectiveContext, userId, videoData.relevanceScore);
      }
      
      return videoId;
    } catch (error) {
      console.error('❌ Erreur ajout vidéo:', error);
      throw error;
    }
  }

  async checkVideoExists(url) {
    try {
      const q = query(
        collection(db, 'videos'),
        where('url', '==', url),
        limit(1)
      );
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.warn('⚠️ Erreur vérification doublon:', error);
      return false;
    }
  }

  async updateVideo(videoId, updates, userId) {
    try {
      const videoRef = doc(db, 'videos', videoId);
      await updateDoc(videoRef, {
        ...updates,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: userId
      });
      
      this.clearCache();
      return true;
    } catch (error) {
      console.error('❌ Erreur mise à jour vidéo:', error);
      throw error;
    }
  }

  async deleteVideo(videoId, userId) {
    try {
      await deleteDoc(doc(db, 'videos', videoId));
      this.clearCache();
      return true;
    } catch (error) {
      console.error('❌ Erreur suppression vidéo:', error);
      throw error;
    }
  }

  async recordVideoView(videoId, userId, theme = null) {
    try {
      if (!userId || !videoId) return;

      // 1️⃣ Enregistrement de la vue dans la collection video_views
      await addDoc(collection(db, 'video_views'), {
        videoId: String(videoId),
        userId: String(userId),
        theme: String(theme || ''),
        viewedAt: new Date().toISOString(),
        timestamp: Date.now()
      });

      // 2️⃣ Mise à jour ou création du document vidéo
      const videoRef = doc(db, 'videos', String(videoId));
      const videoSnap = await getDoc(videoRef);

      if (!videoSnap.exists()) {
        console.log(`📄 Vidéo ${videoId} inexistante. Création d'un document minimal.`);
        await setDoc(videoRef, {
          status: 'draft',
          title: '[auto]',
          totalViews: 0,
          createdAt: new Date().toISOString(),
          lastViewedAt: new Date().toISOString()
        });
      }

      // 3️⃣ Incrément des vues
      await updateDoc(videoRef, {
        totalViews: increment(1),
        lastViewedAt: new Date().toISOString()
      });

      console.log(`👁️ Vue enregistrée pour vidéo ${videoId}`);

    } catch (error) {
      console.warn('⚠️ Erreur dans recordVideoView:', error.message);
    }
  }

  // ================================
  // UTILITAIRES AMÉLIORÉS
  // ================================

  // 🆕 TRANSFORMATION AVEC RÉCUPÉRATION DES SCORES ORIGINAUX (comme NewsFirebaseService)
  transformSourcesToVideos(sources, theme) {
    return sources.map((source, index) => {
      const timestamp = Date.now() - (index * 60000);
      const uniqueId = `${theme}_${timestamp}_${Math.random().toString(36).substr(2, 8)}`;
      
      return {
        id: uniqueId,
        title: source.title || 'Vidéo découverte',
        description: source.description || source.summary || '',
        url: source.usableUrl || source.recommendedUrl || source.url || '#',
        // 🆕 RÉCUPÉRATION IMAGE ORIGINALE (comme dans NewsFirebaseService)
        thumbnail: source.imageUrl || this.generateThumbnail(uniqueId),
        channelTitle: source.author || source.provider || 'Chaîne découverte',
        duration: source.duration || this.generateDuration(),
        provider: source.provider || 'sourceservice',
        viewCount: Number(source.viewCount) || Math.floor(Math.random() * 5000) + 100,
        // 🆕 RÉCUPÉRATION SCORE ORIGINAL (comme dans NewsFirebaseService)
        qualityScore: Number(source.qualityScore) || Number(source.relevanceScore) || 6,
        relevanceScore: Number(source.relevanceScore) || Number(source.qualityScore) || 6,
        // 🆕 MÉTADONNÉES SUPPLÉMENTAIRES
        credibilityScore: Number(source.credibilityScore) || 7,
        publishedAt: source.publishedAt || new Date(timestamp).toISOString(),
        theme: theme,
        language: source.language || 'fr',
        source: 'sourceservice_discovery',
        // 🆕 Détection automatique du thème si possible
        detectedTheme: this.detectThemeFromContent(source.title, source.description) || theme
      };
    }).filter(video => video.title && video.url && video.url !== '#');
  }

  // 🆕 TRANSFORMATION SPÉCIALISÉE POUR OBJECTIFS
  transformSourcesToVideosForObjective(sources, objective) {
    return sources.map((source, index) => {
      const timestamp = Date.now() - (index * 60000);
      const uniqueId = `obj_${objective.id}_${timestamp}_${Math.random().toString(36).substr(2, 8)}`;
      
      return {
        id: uniqueId,
        title: source.title || 'Vidéo découverte',
        description: source.description || source.summary || '',
        url: source.usableUrl || source.recommendedUrl || source.url || '#',
        // 🆕 RÉCUPÉRATION IMAGE ORIGINALE
        thumbnail: source.imageUrl || this.generateThumbnail(uniqueId),
        channelTitle: source.author || source.provider || 'Chaîne découverte',
        duration: source.duration || this.generateDuration(),
        provider: source.provider || 'sourceservice',
        viewCount: Number(source.viewCount) || Math.floor(Math.random() * 5000) + 100,
        // 🆕 RÉCUPÉRATION SCORE ORIGINAL
        qualityScore: Number(source.qualityScore) || Number(source.relevanceScore) || 6,
        relevanceScore: Number(source.relevanceScore) || Number(source.qualityScore) || 6,
        credibilityScore: Number(source.credibilityScore) || 7,
        publishedAt: source.publishedAt || new Date(timestamp).toISOString(),
        language: source.language || 'fr',
        source: 'sourceservice_discovery',
        // 🆕 MÉTADONNÉES SPÉCIFIQUES À L'OBJECTIF
        discoveredForObjective: objective.id,
        objectiveTitle: objective.title,
        objectiveTheme: objective.theme,
        // 🆕 Détection automatique du thème
        theme: this.detectThemeFromContent(source.title, source.description) || 'general'
      };
    }).filter(video => video.title && video.url && video.url !== '#');
  }

  // 🆕 DÉTECTION AUTOMATIQUE DU THÈME
  detectThemeFromContent(title, description) {
    const content = `${title} ${description}`.toLowerCase();
    
    for (const [themeId, themeConfig] of Object.entries(this.themes)) {
      const matchCount = themeConfig.keywords.filter(keyword => 
        content.includes(keyword.toLowerCase())
      ).length;
      
      if (matchCount >= 2) {
        return themeId;
      }
    }
    
    return null; // Pas de thème détecté automatiquement
  }

  enhanceVideoData = (video) => {
    const safeId = video.id || `video_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    
    return {
      ...video,
      id: String(safeId),
      title: String(video.title || 'Formation sans titre'),
      description: String(video.description || ''),
      url: String(video.url || '#'),
      thumbnail: String(video.thumbnail || this.generateThumbnail(safeId)),
      channelTitle: String(video.channelTitle || 'Chaîne éducative'),
      duration: String(video.duration || this.generateDuration()),
      viewCount: Number(video.viewCount || 0),
      qualityScore: Number(video.qualityScore || 5),
      
      // Champs sémantiques
      semanticScore: Number(video.semanticScore || 0),
      semanticJustification: String(video.semanticJustification || ''),
      
      publishedAt: String(video.publishedAt || new Date().toISOString()),
      addedAt: String(video.addedAt || new Date().toISOString()),
      theme: String(video.theme || 'unknown'),
      language: String(video.language || 'fr'),
      source: String(video.source || 'firebase'),
      status: String(video.status || 'active'),
      discoveryMethod: String(video.discoveryMethod || 'manual'),
      isVerified: Boolean(video.isVerified !== false)
    };
  };

  generateThumbnail(videoId) {
    const seed = videoId ? Math.abs(videoId.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) : Math.floor(Math.random() * 1000);
    return `https://picsum.photos/320/180?random=${seed}`;
  }

  generateDuration() {
    const minutes = Math.floor(Math.random() * 20 + 5);
    const seconds = Math.floor(Math.random() * 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // ================================
  // CACHE SIMPLIFIÉ
  // ================================

  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  clearCache(pattern = null) {
    if (pattern) {
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  // ================================
  // STATISTIQUES BASIQUES
  // ================================

  async getGlobalStats() {
    try {
      const allVideos = await this.getAllVideos(300);
      
      const stats = {
        totalVideos: allVideos.length,
        byTheme: {},
        recentlyAdded: 0
      };
      
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      
      allVideos.forEach(video => {
        const theme = video.theme || 'unknown';
        stats.byTheme[theme] = (stats.byTheme[theme] || 0) + 1;
        
        if (video.addedAt && new Date(video.addedAt) > weekAgo) {
          stats.recentlyAdded++;
        }
      });
      
      return stats;
      
    } catch (error) {
      console.error('❌ Erreur getGlobalStats:', error);
      return { totalVideos: 0, byTheme: {}, recentlyAdded: 0 };
    }
  }

  // ================================
  // FONCTION DE NETTOYAGE (MAINTENANCE)
  // ================================

  async cleanupOrphanedObjectiveVideos(userId) {
    try {
      console.log('🧹 Nettoyage des liaisons orphelines...');
      
      const objectiveVideosQuery = query(
        collection(db, 'videos_objective'),
        where('userId', '==', userId)
      );
      
      const snapshot = await getDocs(objectiveVideosQuery);
      let cleanedCount = 0;
      
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const videoExists = await this.getVideoById(data.videoId);
        
        if (!videoExists) {
          await deleteDoc(docSnap.ref);
          cleanedCount++;
        }
      }
      
      console.log(`✅ ${cleanedCount} liaisons orphelines supprimées`);
      return cleanedCount;
      
    } catch (error) {
      console.error('❌ Erreur nettoyage liaisons:', error);
      return 0;
    }
  }

  // ================================
  // INFORMATIONS SERVICE
  // ================================

  getServiceInfo() {
    return {
      cacheSize: this.cache.size,
      cacheTimeout: this.cacheTimeout / 1000 / 60,
      themesConfigured: Object.keys(this.themes).length,
      availableThemes: Object.keys(this.themes),
      version: '4.0.0 - Objective-Independent Discovery Edition',
      features: [
        'Découverte indépendante du thème pour objectifs',
        'Récupération scores et images originales',
        'Liaison automatique vidéo-objectif',
        'Détection automatique de thème',
        'Cache optimisé',
        'Fallback robuste',
        'Gestion CRUD complète',
        'Maintenance des liaisons orphelines'
      ]
    };
  }
}

// Export singleton
const videoFirebaseService = new VideoFirebaseService();
export default videoFirebaseService;